
/**
 * @const spartaContainerSelector
 * @description query selector for Sparta applications
 */
export const spartaContainerSelector = '[data-sparta-wrapper]';

/**
 * @const spartaWidgetContainerString
 * @description String for Sparta widgets
 */
export const spartaWidgetContainerString = 'data-sparta-widget';

/**
 * @const spartaWidgetContainerSelector
 * @description query selector for Sparta widgets
 */
export const spartaWidgetContainerSelector = `[${spartaWidgetContainerString}]`;

/**
 * @const utilityLoadingClass
 * @description String for holding the class name we'll remove from the application container when our utility has initialized
 */
export const utilityLoadingClass = 'sparta-media-query-utility-loading';

/**
 * @const spartaWidgetResizeDebounceDelay
 * @description debounce delay for window.onresize event for widgets
 */
export const spartaWidgetResizeDebounceDelay = 200;

/**
 * @const mediaQueryViewportString
 * @description string used in store to denote viewport media query
 */
export const mediaQueryViewportString = 'viewport';

/**
 * @const mediaQueryOrientationString
 * @description string used in store to denote orientation media query
 */
export const mediaQueryOrientationString = 'orientation';

/**
 * @const mediaQueryResolutionString
 * @description string used in store to denote resolution media query
 */
export const mediaQueryResolutionString = 'resolution';

/**
 * @const mediaQueryUpString
 * @description string used for our *-up class names
 */
export const mediaQueryUpString = 'up';

/**
 * @const mediaQueryOnlyString
 * @description string used for our *-only class names
 */
export const mediaQueryOnlyString = 'only';

/**
 * @const spartaStoreNamespace
 * @description namespace for mediaquery utility data in sparta store
 */
export const spartaStoreNamespace = 'currentMQ';

/**
 * @const spartaStoreMediaQueriesKey
 * @description namespace key for media query booleans
 */
export const spartaStoreMediaQueriesKey = 'mediaQueries';

/**
 * Media Query Definitions
 *
 * We generate media query strings from the following min-width values:
 *   small: 767px and below
 *   medium: 768px -> 1024px
 *   large: 1025px and above
 *   xlarge: 1441px -> 1920px
 *   xxlarge: 1921px and above
 *
 * `largeCenteredMinWidth` is used to for auto margin external gutter on large screens
 */

/**
 * @const smallMinWidth
 * @description min width for small viewport, we start at 1px so matchMedia works
 */
export const smallMinWidth = 1;

/**
 * @const mediumMinWidth
 * @description min width for medium viewport
 */
export const mediumMinWidth = 768;

/**
 * @const largeMinWidth
 * @description min width for large viewport
 */
export const largeMinWidth = 1025;

/**
 * @const largeCenteredMinWidth
 * @description width that is larger than row width + gutters and we need to switch to auto
 */
export const largeCenteredMinWidth = 1316;

/**
 * @const xlargeMinWidth
 * @description min width for xlarge viewport
 */
export const xlargeMinWidth = 1441;

/**
 * @const xxlargeMinWidth
 * @description min width for xxlarge viewport
 */
export const xxlargeMinWidth = 1921;

/**
 * @const smallViewportName
 * @description string to hold small viewport short name
 */
export const smallViewportName = 'small';

/**
 * @const smallViewportString
 * @description string to hold small viewport name
 */
export const smallViewportString = smallViewportName;

/**
 * @const mediumViewportName
 * @description string to hold medium viewport short name
 */
export const mediumViewportName = 'medium';

/**
 * @const mediumViewportString
 * @description string to hold medium viewport name
 */
export const mediumViewportString = mediumViewportName;

/**
 * @const largeViewportName
 * @description string to hold large viewport short name
 */
export const largeViewportName = 'large';

/**
 * @const largeViewportString
 * @description string to hold large viewport name
 */
export const largeViewportString = largeViewportName;

/**
 * @const xlargeViewportName
 * @description string to hold xlarge viewport short name
 */
export const xlargeViewportName = 'xlarge';

/**
 * @const xlargeViewportString
 * @description string to hold xlarge viewport name
 */
export const xlargeViewportString = xlargeViewportName;

/**
 * @const xxlargeViewportName
 * @description string to hold xxlarge viewport short name
 */
export const xxlargeViewportName = 'xxlarge';

/**
 * @const xxlargeViewportString
 * @description string to hold xxlarge viewport name
 */
export const xxlargeViewportString = xxlargeViewportName;

/**
 * @const largeCenteredViewportName
 * @description string to hold name for viewport needed to deal with centering the large grid beyond the max row width
 */
export const largeCenteredViewportName = 'largeCentered';

/**
 * @const landscapeOrientationName
 * @description string to hold landscape orientation name
 */
export const landscapeOrientationName = 'landscape';

/**
 * @const portraitOrientationName
 * @description string to hold portrait orientation name
 */
export const portraitOrientationName = 'portrait';

/**
 * @const defaultResolutionnName
 * @description string to hold default resolution name
 */
export const defaultResolutionName = 'standard';

/**
 * @const retinaResolutionnName
 * @description string to hold retina resolution name
 */
export const retinaResolutionName = 'retina';

/**
 * @const smallMediaQueryString
 * @description string to hold small media query definition
 */
export const smallMediaQueryString = `only screen and (min-width: ${smallMinWidth}px)`;

/**
 * @const mediumMediaQueryString
 * @description string to hold medium media query definition
 */
export const mediumMediaQueryString = `only screen and (min-width: ${mediumMinWidth}px)`;

/**
 * @const largeMediaQueryString
 * @description string to hold large media query definition
 */
export const largeMediaQueryString = `only screen and (min-width: ${largeMinWidth}px)`;

/**
 * @const largeCenteredMediaQueryString
 * @description string to hold largeCentered media query definition
 */
export const largeCenteredMediaQueryString = `only screen and (min-width: ${largeCenteredMinWidth}px)`;

/**
 * @const xlargeMediaQueryString
 * @description string to hold xlarge media query definition
 */
export const xlargeMediaQueryString = `only screen and (min-width: ${xlargeMinWidth}px)`;

/**
 * @const xxlargeMediaQueryString
 * @description string to hold xxlarge media query definition
 */
export const xxlargeMediaQueryString = `only screen and (min-width: ${xxlargeMinWidth}px)`;

/**
 * @const portraitMediaQueryString
 * @description string to hold portrait media query definition
 */
export const portraitMediaQueryString = `only screen and (orientation: ${portraitOrientationName})`;

/**
 * @const landscapeMediaQueryString
 * @description string to hold landscape media query definition
 */
export const landscapeMediaQueryString = `only screen and (orientation: ${landscapeOrientationName})`;

/**
 * @const defaultResolutionMediaQueryString
 * @description string to hold non-retina media query definition
 */
export const defaultResolutionMediaQueryString = `not screen and (min-device-pixel-ratio: 2), not screen and (min-resolution: 192dpi), not screen and (min-resolution: 2dppx)`;

/**
 * @const retinaResolutionMediaQueryString
 * @description string to hold retina media query definition
 */
export const retinaResolutionMediaQueryString = `only screen and (min-device-pixel-ratio: 2), only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx)`;

/**
 * @const viewportNames
 * @description object containing viewport names mapped to simple keys in case they change in the future
 */
export const viewportNames = {
  [smallViewportName]: smallViewportString,
  [mediumViewportName]: mediumViewportString,
  [largeViewportName]: largeViewportString,
  [largeCenteredViewportName]: largeCenteredViewportName,
  [xlargeViewportName]: xlargeViewportString,
  [xxlargeViewportName]: xxlargeViewportString,
};
